/**
 * Format Utilities
 *
 * Shared formatting functions for consistent data presentation across the application.
 */

/**
 * Format bytes to a human-readable string
 *
 * @param bytes - The number of bytes to format
 * @param decimals - Number of decimal places (default: 2)
 * @returns Formatted string like "1.5 GB"
 *
 * @example
 * formatBytes(1024) // "1 KB"
 * formatBytes(1234567890) // "1.15 GB"
 * formatBytes(0) // "0 Bytes"
 */
export function formatBytes(bytes: number, decimals: number = 2): string {
  if (bytes === 0) return "0 B";
  if (bytes < 0) return "-" + formatBytes(Math.abs(bytes), decimals);

  // Clamp decimals to the range accepted by Number.toFixed (0–20)
  const dm = Math.max(0, Math.min(20, Math.floor(decimals)));

  const k = 1024;
  const sizes = ["B", "KB", "MB", "GB", "TB", "PB", "EB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  const index = Math.min(i, sizes.length - 1);

  return (
    parseFloat((bytes / Math.pow(k, index)).toFixed(dm)) + " " + sizes[index]
  );
}

/**
 * Format file size with fixed 1-decimal precision for KB and above.
 *
 * This preserves the output format that many components and tests expect:
 *   formatFileSize(0)            → "0 B"
 *   formatFileSize(1024)         → "1.0 KB"
 *   formatFileSize(1024 * 1024)  → "1.0 MB"
 *   formatFileSize(1.5 * 1e9)   → "1.4 GB"
 *
 * For configurable decimals, use `formatBytes(bytes, decimals)` instead.
 *
 * Canonical location: import from `@/lib/utils/format`.
 */
export function formatFileSize(bytes: number): string {
  if (bytes === 0) return "0 B";
  if (bytes < 0) return "-" + formatFileSize(Math.abs(bytes));

  const k = 1024;
  const sizes = ["B", "KB", "MB", "GB", "TB", "PB", "EB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  const index = Math.min(i, sizes.length - 1);
  const value = bytes / Math.pow(k, index);

  // Bytes: no decimals. Everything else: exactly 1 decimal.
  const formatted =
    index === 0 ? Math.round(value).toString() : value.toFixed(1);
  return `${formatted} ${sizes[index]}`;
}

/**
 * Compact byte formatting: no trailing decimals for small values,
 * 1 decimal for GB+. Used in tight UI layouts (badges, table cells).
 *
 * @example
 * formatBytesCompact(0)            // "0 B"
 * formatBytesCompact(512)          // "512 B"
 * formatBytesCompact(1024 * 1024)  // "1 MB"
 * formatBytesCompact(5.25 * 1e9)   // "4.9 GB"
 */
export function formatBytesCompact(bytes: number): string {
  if (bytes === 0) return "0 B";

  const units = ["B", "KB", "MB", "GB", "TB", "PB"];
  const k = 1024;
  const i = Math.floor(Math.log(Math.abs(bytes)) / Math.log(k));
  const index = Math.min(i, units.length - 1);
  const value = bytes / Math.pow(k, index);

  // Use more decimals for large units (GB+)
  if (index >= 3) {
    return `${value.toFixed(1)} ${units[index]}`;
  }

  // No decimals for small values, 1 decimal for KB/MB if fractional
  const rounded = Math.round(value * 10) / 10;
  const formatted = rounded % 1 === 0 ? rounded.toFixed(0) : rounded.toFixed(1);
  return `${formatted} ${units[index]}`;
}

/**
 * Parse a human-readable byte string back to bytes
 *
 * @param str - String like "1.5 GB" or "500 MB"
 * @returns Number of bytes, or null if invalid
 *
 * @example
 * parseBytes("1 KB") // 1024
 * parseBytes("1.5 GB") // 1610612736
 */
export function parseBytes(str: string): number | null {
  const match = str.trim().match(/^([\d.]+)\s*(bytes?|b|kb|mb|gb|tb|pb|eb)?$/i);
  if (!match) return null;

  const value = parseFloat(match[1]);
  if (isNaN(value)) return null;

  const unit = (match[2] || "bytes").toLowerCase();
  const k = 1024;

  const units: Record<string, number> = {
    b: 1,
    byte: 1,
    bytes: 1,
    kb: k,
    mb: k ** 2,
    gb: k ** 3,
    tb: k ** 4,
    pb: k ** 5,
    eb: k ** 6,
  };

  const multiplier = units[unit];
  if (multiplier === undefined) return null;

  return Math.round(value * multiplier);
}

/**
 * Format a duration in seconds to a human-readable string
 *
 * @param seconds - Duration in seconds
 * @param options - Formatting options
 * @returns Formatted string like "1h 30m 45s" or "01:30:45"
 *
 * @example
 * formatDuration(90) // "1m 30s"
 * formatDuration(3665) // "1h 1m 5s"
 * formatDuration(90, { style: "colon" }) // "01:30"
 */
export function formatDuration(
  seconds: number,
  options: { style?: "compact" | "colon" | "long" } = {},
): string {
  const { style = "compact" } = options;

  if (seconds < 0) return "-" + formatDuration(Math.abs(seconds), options);
  if (seconds === 0) return style === "colon" ? "00:00" : "0s";

  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const secs = Math.floor(seconds % 60);

  if (style === "colon") {
    const parts = [];
    if (hours > 0) {
      parts.push(hours.toString().padStart(2, "0"));
    }
    parts.push(minutes.toString().padStart(2, "0"));
    parts.push(secs.toString().padStart(2, "0"));
    return parts.join(":");
  }

  if (style === "long") {
    const parts = [];
    if (hours > 0) parts.push(`${hours} hour${hours !== 1 ? "s" : ""}`);
    if (minutes > 0) parts.push(`${minutes} minute${minutes !== 1 ? "s" : ""}`);
    if (secs > 0 || parts.length === 0)
      parts.push(`${secs} second${secs !== 1 ? "s" : ""}`);
    return parts.join(" ");
  }

  // compact style
  const parts = [];
  if (hours > 0) parts.push(`${hours}h`);
  if (minutes > 0) parts.push(`${minutes}m`);
  if (secs > 0 || parts.length === 0) parts.push(`${secs}s`);
  return parts.join(" ");
}

/**
 * Format a number with thousand separators
 *
 * @param num - Number to format
 * @param locale - Locale for formatting (default: "en-US")
 * @returns Formatted string like "1,234,567"
 *
 * @example
 * formatNumber(1234567) // "1,234,567"
 */
export function formatNumber(num: number, locale: string = "en-US"): string {
  return new Intl.NumberFormat(locale).format(num);
}

/**
 * Format a percentage value
 *
 * @param value - The decimal value (0-1) or percentage (0-100)
 * @param options - Formatting options
 * @returns Formatted string like "75.5%"
 *
 * @example
 * formatPercentage(0.755) // "75.5%"
 * formatPercentage(75.5, { isDecimal: false }) // "75.5%"
 */
export function formatPercentage(
  value: number,
  options: { decimals?: number; isDecimal?: boolean } = {},
): string {
  const { decimals = 1, isDecimal = true } = options;
  const percentage = isDecimal ? value * 100 : value;
  return `${percentage.toFixed(decimals)}%`;
}

/**
 * Format a date to a relative time string
 *
 * @param date - Date to format
 * @param baseDate - Base date for comparison (default: now)
 * @returns Formatted string like "2 hours ago" or "in 3 days"
 *
 * @example
 * formatRelativeTime(new Date(Date.now() - 3600000)) // "1 hour ago"
 */
export function formatRelativeTime(
  date: Date | string | number,
  baseDate: Date = new Date(),
): string {
  const d = new Date(date);
  const diff = baseDate.getTime() - d.getTime();
  const absDiff = Math.abs(diff);
  const isPast = diff > 0;

  const seconds = Math.floor(absDiff / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);
  const weeks = Math.floor(days / 7);
  const months = Math.floor(days / 30);
  const years = Math.floor(days / 365);

  let value: number;
  let unit: string;

  if (seconds < 60) {
    value = seconds;
    unit = "second";
  } else if (minutes < 60) {
    value = minutes;
    unit = "minute";
  } else if (hours < 24) {
    value = hours;
    unit = "hour";
  } else if (days < 7) {
    value = days;
    unit = "day";
  } else if (weeks < 4) {
    value = weeks;
    unit = "week";
  } else if (months < 12) {
    value = months;
    unit = "month";
  } else {
    value = years;
    unit = "year";
  }

  const plural = value !== 1 ? "s" : "";

  if (isPast) {
    return value === 0 ? "just now" : `${value} ${unit}${plural} ago`;
  } else {
    return `in ${value} ${unit}${plural}`;
  }
}

/**
 * Truncate a string to a maximum length with ellipsis
 *
 * @param str - String to truncate
 * @param maxLength - Maximum length including ellipsis
 * @param ellipsis - Ellipsis string (default: "...")
 * @returns Truncated string
 *
 * @example
 * truncateString("Hello World", 8) // "Hello..."
 */
export function truncateString(
  str: string,
  maxLength: number,
  ellipsis: string = "...",
): string {
  if (str.length <= maxLength) return str;
  return str.slice(0, maxLength - ellipsis.length) + ellipsis;
}

/**
 * Format seconds as a compact "M:SS" string (no zero-padded minutes).
 *
 * This is the short display format used in time-range error messages
 * (e.g. "2:05" for 125 seconds). For padded/long formats use {@link formatDuration}.
 *
 * @param seconds - Duration in seconds (fractional parts are floored)
 * @returns Formatted string like "0:00", "1:30", "12:05"
 *
 * @example
 * formatTimeMinSec(0)   // "0:00"
 * formatTimeMinSec(65)  // "1:05"
 * formatTimeMinSec(600) // "10:00"
 */
export function formatTimeMinSec(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${m}:${s.toString().padStart(2, "0")}`;
}

/**
 * Clamp an optional end-time so it never exceeds the source video duration.
 *
 * Returns the original `endTime` when no clamping is needed, or the
 * `sourceDuration` when `endTime` overshoots.  Passing `undefined` for
 * either value is safe — the function simply returns `endTime` unchanged.
 *
 * @param endTime          - The requested end time in seconds (may be undefined)
 * @param sourceDuration   - Total duration of the source video in seconds (may be undefined)
 * @returns The (possibly clamped) end time, or `undefined` when `endTime` was undefined
 *
 * @example
 * clampEndTime(120, 60)        // 60
 * clampEndTime(30, 60)         // 30
 * clampEndTime(undefined, 60)  // undefined
 * clampEndTime(120, undefined) // 120
 */
export function clampEndTime(
  endTime: number | undefined,
  sourceDuration: number | undefined,
): number | undefined {
  if (
    endTime !== undefined &&
    sourceDuration !== undefined &&
    sourceDuration > 0 &&
    endTime > sourceDuration
  ) {
    return sourceDuration;
  }
  return endTime;
}

/**
 * Format a file name by truncating the middle if too long
 *
 * @param fileName - File name to format
 * @param maxLength - Maximum length
 * @returns Formatted file name preserving extension
 *
 * @example
 * formatFileName("very-long-file-name.mp4", 15) // "very-...e.mp4"
 */
export function formatFileName(fileName: string, maxLength: number): string {
  if (fileName.length <= maxLength) return fileName;

  const lastDot = fileName.lastIndexOf(".");
  const ext = lastDot > 0 ? fileName.slice(lastDot) : "";
  const name = lastDot > 0 ? fileName.slice(0, lastDot) : fileName;

  const availableLength = maxLength - ext.length - 3; // 3 for "..."
  if (availableLength <= 0) return truncateString(fileName, maxLength);

  const frontLength = Math.ceil(availableLength / 2);
  const backLength = Math.floor(availableLength / 2);

  return name.slice(0, frontLength) + "..." + name.slice(-backLength) + ext;
}
